// Canonical signal taxonomy + derivation contributions. Single source of truth
// for the signal-type vocabulary, each type's active|reserved status, and the
// stage/disposition contributions consumed by deriveProjectStatus and the UI.
// Dependency-light by design (no @parcel/db); the DB enum + columns are aligned
// to these values via the stage-ladder migration.

export const CANONICAL_SIGNAL_TYPE_VALUES = [
  'project_announcement',
  'pre_application',
  'site_acquisition',
  'demolition_permit',
  'site_prep',
  'entitlement_application',
  'environmental_review',
  'public_review',
  'rfp',
  'entitlement_approval',
  'entitlement_denial',
  'withdrawal',
  'expiration',
  'permit_filed',
  'permit_issued',
  'construction_loan',
  'under_construction',
  'team_update',
  'completion',
] as const;
export type CanonicalSignalType = (typeof CANONICAL_SIGNAL_TYPE_VALUES)[number];

// The one status axis: a 7-rung funnel from early signals to completion.
// Rank = index + 1; null = Unknown (rank 0), a valid outcome.
export const CANONICAL_STAGE_VALUES = [
  'pre_filing',
  'filed',
  'under_review',
  'approved',
  'permitted',
  'under_construction',
  'completed',
] as const;
export type CanonicalStage = (typeof CANONICAL_STAGE_VALUES)[number];

export const DISPOSITION_VALUES = [
  'active',
  'denied',
  'withdrawn',
  'expired',
  'stalled',
  'cancelled',
] as const;
export type Disposition = (typeof DISPOSITION_VALUES)[number];

/** Display labels + descriptions for the canonical disposition vocabulary
 *  (consumer UI tooltips, API/MCP). */
export const DISPOSITION_DEFINITIONS: readonly {
  value: Disposition;
  label: string;
  description: string;
}[] = [
  { value: 'active', label: 'Active', description: 'Live and progressing through the funnel.' },
  {
    value: 'denied',
    label: 'Denied',
    description: 'The application was rejected by the authority.',
  },
  { value: 'withdrawn', label: 'Withdrawn', description: 'The applicant pulled the application.' },
  { value: 'expired', label: 'Expired', description: 'A granted approval has lapsed.' },
  {
    value: 'stalled',
    label: 'Stalled',
    description: 'No qualifying activity for an extended period.',
  },
  { value: 'cancelled', label: 'Cancelled', description: 'The project was explicitly cancelled.' },
];

// The dispositions a single terminal signal sets directly. 'stalled' is
// time-derived; 'cancelled' needs an explicit cancellation/superseding signal.
export type TerminalDisposition = Extract<Disposition, 'denied' | 'withdrawn' | 'expired'>;

export const SIGNAL_STATE_VALUES = ['resolved', 'unclassified'] as const;
export type SignalState = (typeof SIGNAL_STATE_VALUES)[number];

export const TRACK_VALUES = ['funnel', 'non_funnel'] as const;
export type Track = (typeof TRACK_VALUES)[number];

export interface SignalTypeRegistryEntry {
  /** false = reserved: a valid event with no live source yet. */
  active: boolean;
  /** Human-readable short label for the signal type. */
  label: string;
  /** One-sentence description of what this signal represents. */
  description: string;
  /** Stage this type drives, or null if it does not advance Stage. */
  stage: CanonicalStage | null;
  /** If set, a terminal/off-ramp signal that sets Disposition without advancing Stage. */
  terminalDisposition: TerminalDisposition | null;
  /** Whether users can subscribe to notifications for this signal type. */
  subscribable: boolean;
  /** Suggested notification priority, or null if no specific priority recommendation. */
  recommendedPriority: 'high' | 'medium' | 'low' | null;
  /** Registry-declared bound (in days) on how far a signal's filed_at may sit in
   *  the future before the signal-event resolver parks it in needs_review instead
   *  of writing it to canonical signals. Omitted/undefined = the resolver's default
   *  1-day tolerance applies. Set only for types whose source legitimately reports
   *  a scheduled future event (e.g. public_review hearing dates). */
  maxFutureDays?: number;
}

export const SIGNAL_TYPE_REGISTRY: Record<CanonicalSignalType, SignalTypeRegistryEntry> = {
  project_announcement: {
    active: true,
    label: 'Project announcement',
    description:
      'A public announcement that a project is planned, such as a developer press release, community notice, or city filing establishing that a project exists before any formal application.',
    stage: 'pre_filing',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: null,
  },
  pre_application: {
    active: true,
    label: 'Pre-application',
    description:
      'A pre-application meeting, letter of intent, or informal submission to the planning authority before a formal application is filed. Indicates a project is moving toward the entitlement process.',
    stage: 'pre_filing',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: null,
  },
  site_acquisition: {
    active: true,
    label: 'Site acquisition',
    description:
      'A recorded deed transfer or announced purchase agreement establishing developer site control. Often one of the earliest reliable signals that a project is real and funded.',
    stage: 'pre_filing',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: 'medium',
  },
  demolition_permit: {
    active: true,
    label: 'Demolition permit',
    description:
      'A permit to demolish existing structures on the site. Often precedes a building permit by weeks or months and signals that construction is approaching.',
    stage: 'permitted',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: null,
  },
  site_prep: {
    active: true,
    label: 'Site prep',
    description:
      'Early site-preparation permits (geotechnical investigations, soil borings, excavation, or shoring) indicating that ground work is underway before a full building permit is issued.',
    stage: 'permitted',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: null,
  },
  entitlement_application: {
    active: true,
    label: 'Entitlement application',
    description:
      'A formal application for a discretionary land-use approval: Article 80 large or small project review, special permit, variance, rezoning, or site plan review. The core regulatory milestone most significant projects must pass through.',
    stage: 'filed',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: null,
  },
  environmental_review: {
    active: true,
    label: 'Environmental review',
    description:
      'An environmental notification form (ENF), environmental impact report (EIR), or other state or federal environmental review filing (MEPA, NEPA) associated with the project.',
    stage: 'filed',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: null,
  },
  public_review: {
    active: true,
    label: 'Public review',
    description:
      'The application has entered active public review: a hearing has been scheduled, staff memos published, or a public comment period opened.',
    stage: 'under_review',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: null,
    // Sources (e.g. Somerville's list) carry scheduled future hearing dates;
    // 180 days covers a realistic scheduling horizon without opening the guard
    // to arbitrary future dates.
    maxFutureDays: 180,
  },
  rfp: {
    active: false,
    label: 'RFP',
    description:
      'A solicitation for design or engineering services issued by the developer or a public agency. Reserved: not yet active in Parcel data.',
    stage: null,
    terminalDisposition: null,
    subscribable: false,
    recommendedPriority: null,
  },
  entitlement_approval: {
    active: true,
    label: 'Entitlement approval',
    description:
      'The discretionary land-use approval was granted by the relevant authority. A major milestone indicating the project has cleared its primary regulatory hurdle and is likely to move toward permitting.',
    stage: 'approved',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: 'high',
  },
  entitlement_denial: {
    active: true,
    label: 'Entitlement denial',
    description:
      'The discretionary land-use application was rejected. The developer may refile with modifications; projects with a denial are not necessarily dead.',
    stage: null,
    terminalDisposition: 'denied',
    subscribable: true,
    recommendedPriority: null,
  },
  withdrawal: {
    active: true,
    label: 'Withdrawal',
    description:
      'The applicant voluntarily withdrew the application from review. May indicate a project is being redesigned, paused, or abandoned.',
    stage: null,
    terminalDisposition: 'withdrawn',
    subscribable: true,
    recommendedPriority: null,
  },
  expiration: {
    active: true,
    label: 'Expiration',
    description:
      'A previously granted approval lapsed because the developer did not act on it within the required timeframe. The project would need to refile to proceed.',
    stage: null,
    terminalDisposition: 'expired',
    subscribable: true,
    recommendedPriority: null,
  },
  permit_filed: {
    active: true,
    label: 'Permit filed',
    description:
      'A building permit application has been submitted to the building department. Distinct from permit_issued: the permit is applied for but not yet approved.',
    stage: null,
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: 'medium',
  },
  permit_issued: {
    active: true,
    label: 'Permit issued',
    description:
      'A building permit has been officially issued. Construction is now legally authorized to begin.',
    stage: 'permitted',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: 'high',
  },
  construction_loan: {
    active: true,
    label: 'Construction loan',
    description:
      'A construction loan has been recorded in the public record. Financing is in place and construction is typically imminent.',
    stage: 'permitted',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: 'low',
  },
  under_construction: {
    active: true,
    label: 'Under construction',
    description:
      'Construction activity has begun on site, confirmed through site activity reports, inspection records, or a verified construction-start notice.',
    stage: 'under_construction',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: 'high',
  },
  team_update: {
    active: true,
    label: 'Team update',
    description:
      'A development team member has been publicly named or changed in a filing, such as a new architect added, a GC named, or a project manager listed. Does not advance the project stage.',
    stage: null,
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: null,
  },
  completion: {
    active: true,
    label: 'Completion',
    description: 'Construction has been completed or a certificate of occupancy has been issued.',
    stage: 'completed',
    terminalDisposition: null,
    subscribable: true,
    recommendedPriority: null,
  },
};

/** Stage rank (forward-only). null = no stage = 0. */
export function stageRank(stage: CanonicalStage | null): number {
  return stage == null ? 0 : CANONICAL_STAGE_VALUES.indexOf(stage) + 1;
}

/** Rank lookup keyed by value, for the rail's evidenced-vs-implied helper. */
export const STAGE_RANK_BY_VALUE: Record<CanonicalStage, number> = Object.fromEntries(
  CANONICAL_STAGE_VALUES.map((value, index) => [value, index + 1]),
) as Record<CanonicalStage, number>;
